import React, { useState, useEffect, useRef, useMemo } from 'react';
import { apiFetch } from '../utils/api';
import { useSalesMembers } from '../hooks/useSalesMembers';
import {
  getCurrentYearMonth,
  shouldShowInSalesMonthKanban,
  countPpInProgress,
  countWonInMonth,
  getPpInterviewStatsForMonth,
  parseHistoryTimestamp,
  getStatusChangeTimestamp,
  normalizeSalesStatus,
} from '../utils/salesProgress';
import {
  PageHeader,
  Button,
  Badge,
  badgeVariantForLabel,
  LoadingState,
  Card,
  KpiCard,
  KanbanBoard,
  KanbanColumn,
  KanbanColumnEmpty,
  SalesKanbanCard,
  SalesMonthFilterSelect,
  SalesProgressFiltersGrid,
  SalesProgressFilterField,
  PersonFilterSelect,
  SalesProgressStatsSection,
  Alert,
} from './ui';

const inputFocus =
  'focus:ring-2 focus:ring-prodia-primary/30 focus:border-prodia-primary focus:outline-none';

// エンジニア名オートコンプリート入力コンポーネント
function EngineerNameAutocomplete({ value, onChange, engineerNames, className, placeholder }) {
  const [suggestions, setSuggestions] = React.useState([]);
  const [showSuggestions, setShowSuggestions] = React.useState(false);
  const [activeIndex, setActiveIndex] = React.useState(-1);
  const wrapperRef = React.useRef(null);

  // 外側クリックで候補を閉じる
  React.useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowSuggestions(false);
        setActiveIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleChange = (e) => {
    const input = e.target.value;
    onChange(input);
    if (input.trim()) {
      const filtered = engineerNames.filter(name =>
        name.includes(input) || name.toLowerCase().includes(input.toLowerCase())
      );
      setSuggestions(filtered.slice(0, 8));
      setShowSuggestions(filtered.length > 0);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
    setActiveIndex(-1);
  };

  const handleSelect = (name) => {
    onChange(name);
    setSuggestions([]);
    setShowSuggestions(false);
    setActiveIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(prev => Math.min(prev + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(prev => Math.max(prev - 1, -1));
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      handleSelect(suggestions[activeIndex]);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
      setActiveIndex(-1);
    }
  };

  return (
    <div ref={wrapperRef} className="relative">
      <input
        type="text"
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          if (value.trim() && suggestions.length > 0) setShowSuggestions(true);
        }}
        className={className}
        placeholder={placeholder || 'エンジニア名を入力'}
        autoComplete="off"
      />
      {showSuggestions && suggestions.length > 0 && (
        <ul className="absolute z-50 w-full bg-white border border-slate-200 rounded-xl shadow-lg mt-1 overflow-hidden max-h-56 overflow-y-auto">
          {suggestions.map((name, idx) => (
            <li
              key={name}
              onMouseDown={() => handleSelect(name)}
              className={`px-4 py-2 cursor-pointer text-sm transition-colors ${
                idx === activeIndex
                  ? 'bg-prodia-primary text-prodia-primary-foreground'
                  : 'text-slate-700 hover:bg-prodia-background'
              }`}
            >
              <i className="fas fa-user mr-2 text-xs opacity-50"></i>
              {name}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// カンバンボードのカラム定義
const KANBAN_COLUMNS = [
  { id: 'scheduling', title: '日程調整中',        icon: 'fa-calendar-plus' },
  { id: 'scheduled',  title: '面談予定',           icon: 'fa-calendar-check' },
  { id: 'completed',  title: '面談済み／回答待ち',  icon: 'fa-handshake' },
  { id: 'won',        title: '成約',               icon: 'fa-trophy' },
  { id: 'lost',       title: '見送り',             icon: 'fa-times-circle' },
];

// ステータスとカラムのマッピング
const STATUS_TO_COLUMN = {
  '日程調整中':         'scheduling',
  '日程調整中（1/2）':  'scheduling',
  '日程調整中（2/2）':  'scheduling',
  '面談予定':           'scheduled',
  '面談予定（1/2）':    'scheduled',
  '面談予定（2/2）':    'scheduled',
  '面談済み／回答待ち':         'completed',
  '面談済み／回答待ち（1/2）':  'completed',
  '面談済み／回答待ち（2/2）':  'completed',
  '成約':    'won',
  '見送り':  'lost',
  'お見送り': 'lost',
  // 旧ステータス（後方互換）
  '面談済み':               'completed',
  '回答待ち':               'completed',
  '面談済み（1/2）／回答待ち': 'completed',
  '面談済み（2/2）／回答待ち': 'completed',
};

const COLUMN_TO_STATUS = {
  'scheduling': '日程調整中',
  'scheduled':  '面談予定',
  'completed':  '面談済み／回答待ち',
  'won':  '成約',
  'lost': '見送り'
};

// 単価表示用: 数値文字列に3桁コンマを挿入
const formatUnitPrice = (val) => {
  if (val === '' || val === null || val === undefined) return '';
  const raw = String(val).replace(/,/g, '');
  if (!/^\d*\.?\d*$/.test(raw)) return val;
  const [int, dec] = raw.split('.');
  const formatted = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return dec !== undefined ? `${formatted}.${dec}` : formatted;
};

// 日付入力（手入力 YYYY/MM/DD + カレンダー選択）
function SmartDateInput({ value, onChange, name, className }) {
  const [displayValue, setDisplayValue] = React.useState('');
  const textRef = React.useRef(null);
  const pickerRef = React.useRef(null);

  React.useEffect(() => {
    if (value) {
      const p = value.split('-');
      setDisplayValue(p.length === 3 ? `${p[0]}/${p[1]}/${p[2]}` : value);
    } else {
      setDisplayValue('');
    }
  }, [value]);

  const handleText = (e) => {
    const cleaned = e.target.value.replace(/[^0-9/]/g, '');
    const digits = cleaned.replace(/\//g, '').slice(0, 8);
    let fmt = '';
    if (digits.length <= 4) fmt = digits;
    else if (digits.length <= 6) fmt = `${digits.slice(0,4)}/${digits.slice(4)}`;
    else fmt = `${digits.slice(0,4)}/${digits.slice(4,6)}/${digits.slice(6,8)}`;
    setDisplayValue(fmt);
    if (digits.length === 8) onChange({ target: { name, value: `${digits.slice(0,4)}-${digits.slice(4,6)}-${digits.slice(6,8)}` } });
    else if (digits.length === 0) onChange({ target: { name, value: '' } });
  };

  React.useEffect(() => {
    const el = textRef.current;
    if (el && document.activeElement === el) el.setSelectionRange(displayValue.length, displayValue.length);
  }, [displayValue]);

  const openCalendar = () => {
    try { pickerRef.current?.showPicker(); } catch { pickerRef.current?.focus(); }
  };

  return (
    <div className="relative">
      <input ref={textRef} type="text" value={displayValue} onChange={handleText}
        className={`${className} pr-10`} placeholder="YYYY/MM/DD" maxLength={10} inputMode="numeric" autoComplete="off" />
      <button type="button" onClick={openCalendar} tabIndex={-1}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors">
        <i className="fas fa-calendar-alt"></i>
      </button>
      <input ref={pickerRef} type="date" value={value || ''} tabIndex={-1} aria-hidden="true"
        onChange={(e) => { const p = e.target.value.split('-'); if (p.length===3) { setDisplayValue(`${p[0]}/${p[1]}/${p[2]}`); onChange({ target: { name, value: e.target.value } }); } }}
        className="absolute opacity-0 pointer-events-none w-0 h-0 overflow-hidden" />
    </div>
  );
}

// 時間入力（手入力 HH:MM + ピッカー選択）
function SmartTimeInput({ value, onChange, name, className }) {
  const [displayValue, setDisplayValue] = React.useState('');
  const textRef = React.useRef(null);
  const pickerRef = React.useRef(null);

  React.useEffect(() => { setDisplayValue(value || ''); }, [value]);

  const handleText = (e) => {
    const digits = e.target.value.replace(/[^0-9:]/g, '').replace(/:/g, '').slice(0, 4);
    const fmt = digits.length <= 2 ? digits : `${digits.slice(0,2)}:${digits.slice(2)}`;
    setDisplayValue(fmt);
    if (digits.length === 4) onChange({ target: { name, value: `${digits.slice(0,2)}:${digits.slice(2,4)}` } });
    else if (digits.length === 0) onChange({ target: { name, value: '' } });
  };

  React.useEffect(() => {
    const el = textRef.current;
    if (el && document.activeElement === el) el.setSelectionRange(displayValue.length, displayValue.length);
  }, [displayValue]);

  const openPicker = () => {
    try { pickerRef.current?.showPicker(); } catch { pickerRef.current?.focus(); }
  };

  return (
    <div className="relative">
      <input ref={textRef} type="text" value={displayValue} onChange={handleText}
        className={`${className} pr-10`} placeholder="HH:MM" maxLength={5} inputMode="numeric" autoComplete="off" />
      <button type="button" onClick={openPicker} tabIndex={-1}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors">
        <i className="fas fa-clock"></i>
      </button>
      <input ref={pickerRef} type="time" value={value || ''} tabIndex={-1} aria-hidden="true"
        onChange={(e) => { setDisplayValue(e.target.value); onChange({ target: { name, value: e.target.value } }); }}
        className="absolute opacity-0 pointer-events-none w-0 h-0 overflow-hidden" />
    </div>
  );
}

// 年月入力（手入力 YYYY/MM + ピッカー選択）
function SmartMonthInput({ value, onChange, name, className }) {
  const [displayValue, setDisplayValue] = React.useState('');
  const textRef = React.useRef(null);
  const pickerRef = React.useRef(null);

  React.useEffect(() => {
    if (value) {
      const p = value.split('-');
      setDisplayValue(p.length === 2 ? `${p[0]}/${p[1]}` : value);
    } else {
      setDisplayValue('');
    }
  }, [value]);

  const handleText = (e) => {
    const digits = e.target.value.replace(/[^0-9/]/g, '').replace(/\//g, '').slice(0, 6);
    const fmt = digits.length <= 4 ? digits : `${digits.slice(0,4)}/${digits.slice(4)}`;
    setDisplayValue(fmt);
    if (digits.length === 6) onChange({ target: { name, value: `${digits.slice(0,4)}-${digits.slice(4,6)}` } });
    else if (digits.length === 0) onChange({ target: { name, value: '' } });
  };

  React.useEffect(() => {
    const el = textRef.current;
    if (el && document.activeElement === el) el.setSelectionRange(displayValue.length, displayValue.length);
  }, [displayValue]);

  const openPicker = () => {
    try { pickerRef.current?.showPicker(); } catch { pickerRef.current?.focus(); }
  };

  return (
    <div className="relative">
      <input ref={textRef} type="text" value={displayValue} onChange={handleText}
        className={`${className} pr-10`} placeholder="YYYY/MM" maxLength={7} inputMode="numeric" autoComplete="off" />
      <button type="button" onClick={openPicker} tabIndex={-1}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors">
        <i className="fas fa-calendar-alt"></i>
      </button>
      <input ref={pickerRef} type="month" value={value || ''} tabIndex={-1} aria-hidden="true"
        onChange={(e) => { const p = e.target.value.split('-'); if (p.length===2) { setDisplayValue(`${p[0]}/${p[1]}`); onChange({ target: { name, value: e.target.value } }); } }}
        className="absolute opacity-0 pointer-events-none w-0 h-0 overflow-hidden" />
    </div>
  );
}

const normalizeStatus = normalizeSalesStatus;

const formatInterviewDate = (dateValue, timeValue) => {
  if (!dateValue) return '未定';
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return String(dateValue);
  const formatted = date.toLocaleDateString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
  return timeValue ? `${formatted} ${timeValue}` : formatted;
};

const formatStartMonth = (monthValue) => {
  if (!monthValue || monthValue === '未定') return '未定';
  const parts = String(monthValue).split('-');
  if (parts.length !== 2) return String(monthValue);
  return `${parts[0]}年${Number(parts[1])}月`;
};

const parseHistoryTimestampLocal = parseHistoryTimestamp;

const getLatestRecordTimestamp = (interview) => {
  const candidates = [interview.updated_at, interview.created_at]
    .map(parseHistoryTimestampLocal)
    .filter(Boolean);
  const historyCandidates = (interview.history || [])
    .map(entry => parseHistoryTimestampLocal(entry.timestamp))
    .filter(Boolean);
  const allCandidates = [...candidates, ...historyCandidates];
  return allCandidates.length > 0 ? Math.max(...allCandidates) : 0;
};

const getStatusChangeTimestampLocal = getStatusChangeTimestamp;

const getInterviewSortTimestamp = (interview) => {
  if (!interview.interview_date) return null;
  const timeValue = interview.interview_time || '00:00';
  const parsed = new Date(`${interview.interview_date}T${timeValue}:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed.getTime();
};

const compareInterviewsByColumn = (columnId, a, b) => {
  if (columnId === 'won' || columnId === 'lost') {
    const aWon = getStatusChangeTimestampLocal(a, columnId === 'won' ? ['成約'] : ['見送り', 'お見送り']) || getLatestRecordTimestamp(a);
    const bWon = getStatusChangeTimestampLocal(b, columnId === 'won' ? ['成約'] : ['見送り', 'お見送り']) || getLatestRecordTimestamp(b);
    return bWon - aWon;
  }

  const aTime = getInterviewSortTimestamp(a);
  const bTime = getInterviewSortTimestamp(b);
  if (aTime !== null && bTime !== null) return aTime - bTime;
  if (aTime !== null) return -1;
  if (bTime !== null) return 1;
  return getLatestRecordTimestamp(b) - getLatestRecordTimestamp(a);
};

export default function PPSalesProgress() {
  const { ppMembers, error: membersError } = useSalesMembers();
  const salesPersonOptions = useMemo(
    () => ppMembers.map((member) => member.display_name),
    [ppMembers],
  );

  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewInterviewModal, setShowNewInterviewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [archiveStatus, setArchiveStatus] = useState('成約');
  const [selectedInterview, setSelectedInterview] = useState(null);
  const [editingInterview, setEditingInterview] = useState(null);
  const [selectedSalesMonth, setSelectedSalesMonth] = useState(getCurrentYearMonth);
  const [selectedSales, setSelectedSales] = useState('all');
  const [selectedStatsMonth, setSelectedStatsMonth] = useState(getCurrentYearMonth);
  const [draggedItem, setDraggedItem] = useState(null);
  
  // 自動スクロール用のref
  const scrollContainerRef = useRef(null);
  const autoScrollIntervalRef = useRef(null);

  // IDRエンジニア名リスト（オートコンプリート用）
  const [engineerNames, setEngineerNames] = useState([]);

  // 新規面談フォーム
  const [newInterview, setNewInterview] = useState({
    engineer_name: '',
    company_name: '',
    interview_date: '',
    interview_time: '',
    sales_person: '',
    status: '',
    start_month: '',
    response_deadline: '',
    unit_price: '',
    notes: ''
  });
  const [newInterviewErrors, setNewInterviewErrors] = useState({});

  // 初回マウント時にAPIからデータ取得
  useEffect(() => {
    apiFetch('/pp-interviews/')
      .then(res => res.json())
      .then(data => {
        setInterviews(Array.isArray(data) ? data : (data.results || []));
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  // IDRエンジニア名リスト取得
  useEffect(() => {
    apiFetch('/engineers/')
      .then(res => res.json())
      .then(data => {
        const list = Array.isArray(data) ? data : (data.results || []);
        setEngineerNames(list.map(e => e.name).filter(Boolean).sort());
      })
      .catch(() => {});
  }, []);

  const archiveInterviews = interviews
    .filter(interview => normalizeStatus(interview.status) === archiveStatus)
    .slice()
    .sort((a, b) => getLatestRecordTimestamp(b) - getLatestRecordTimestamp(a));

  // ドラッグ&ドロップハンドラー
  const handleDragStart = (e, interview) => {
    setDraggedItem(interview);
    e.dataTransfer.effectAllowed = 'move';
  };

  // ドラッグ中の自動スクロール処理
  const handleDrag = (e) => {
    if (!scrollContainerRef.current || !draggedItem) return;
    
    const container = scrollContainerRef.current;
    const rect = container.getBoundingClientRect();
    const scrollThreshold = 100; // スクロール開始までの距離（px）
    const scrollSpeed = 10; // スクロール速度
    
    // マウスがコンテナの左端に近い場合
    if (e.clientX - rect.left < scrollThreshold && e.clientX > 0) {
      if (!autoScrollIntervalRef.current) {
        autoScrollIntervalRef.current = setInterval(() => {
          container.scrollLeft -= scrollSpeed;
        }, 16);
      }
    }
    // マウスがコンテナの右端に近い場合
    else if (rect.right - e.clientX < scrollThreshold && e.clientX > 0) {
      if (!autoScrollIntervalRef.current) {
        autoScrollIntervalRef.current = setInterval(() => {
          container.scrollLeft += scrollSpeed;
        }, 16);
      }
    }
    // 自動スクロールを停止
    else {
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current);
        autoScrollIntervalRef.current = null;
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, columnId) => {
    e.preventDefault();
    if (!draggedItem) return;

    const newStatus = COLUMN_TO_STATUS[columnId];
    if (draggedItem.status === newStatus) {
      setDraggedItem(null);
      return;
    }

    const now = new Date();
    const timestamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    const newHistory = [
      ...(draggedItem.history || []),
      { timestamp, user: draggedItem.sales_person, changes: [{ field: 'ステータス', old: draggedItem.status, new: newStatus }] }
    ];

    apiFetch(`/pp-interviews/${draggedItem.id}/`, {
      method: 'PATCH',
      body: JSON.stringify({ status: newStatus, history: newHistory }),
    }).then(res => res.json()).then(updated => {
      setInterviews(prev => prev.map(i => i.id === updated.id ? updated : i));
    });

    setDraggedItem(null);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    // 自動スクロールを停止
    if (autoScrollIntervalRef.current) {
      clearInterval(autoScrollIntervalRef.current);
      autoScrollIntervalRef.current = null;
    }
  };

  // コンポーネントのアンマウント時にクリーンアップ
  useEffect(() => {
    return () => {
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current);
      }
    };
  }, []);

  // カンバンボード用フィルタリング（進行中=常時表示、成約/見送り=営業月内実績）
  const filteredInterviews = interviews.filter(interview => {
    const monthMatch = shouldShowInSalesMonthKanban(interview, selectedSalesMonth);
    const salesMatch = selectedSales === 'all' || interview.sales_person === selectedSales;
    return monthMatch && salesMatch;
  });

  const ppInterviewStats = getPpInterviewStatsForMonth(
    interviews,
    selectedStatsMonth,
    'sales_person',
    selectedSales
  );
  const interviewCountForStats = ppInterviewStats.totalInterviews;
  const engineersForStats = ppInterviewStats.uniqueEngineers;
  const inProgressCountForStats = countPpInProgress(interviews, 'sales_person', selectedSales);
  const wonCountForStats = countWonInMonth(interviews, selectedStatsMonth, 'sales_person', selectedSales);

  // カラムごとにグループ化
  const getInterviewsByColumn = (columnId) => {
    // 旧ステータスも含めて後方互換でマッチ
    return filteredInterviews
      .filter(interview => {
      const col = STATUS_TO_COLUMN[interview.status];
      return col === columnId;
      })
      .slice()
      .sort((a, b) => compareInterviewsByColumn(columnId, a, b));
  };

  // 新規面談追加
  const handleAddInterview = () => {
    const errors = {};
    if (!newInterview.engineer_name.trim()) errors.engineer_name = 'エンジニア名は必須です';
    if (!newInterview.company_name.trim()) errors.company_name = '企業名は必須です';
    if (!newInterview.sales_person) errors.sales_person = '営業担当は必須です';
    if (!newInterview.status) errors.status = 'ステータスは必須です';
    if (!newInterview.start_month) errors.start_month = '開始月は必須です';
    if (Object.keys(errors).length > 0) {
      setNewInterviewErrors(errors);
      return;
    }
    setNewInterviewErrors({});
    const now = new Date();
    const timestamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    const payload = {
      ...newInterview,
      history: [{ timestamp, user: newInterview.sales_person || '不明', changes: [{ field: '作成', old: '', new: '新規登録' }] }]
    };
    apiFetch('/pp-interviews/', { method: 'POST', body: JSON.stringify(payload) })
      .then(res => res.json())
      .then(created => {
        setInterviews(prev => [created, ...prev]);
        setShowNewInterviewModal(false);
        setNewInterviewErrors({});
        setNewInterview({ engineer_name: '', company_name: '', interview_date: '', interview_time: '', sales_person: '', status: '', start_month: '', response_deadline: '', unit_price: '', notes: '' });
      });
  };

  // 編集処理
  const handleEditInterview = () => {
    if (!editingInterview) return;
    const now = new Date();
    const timestamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    const originalInterview = interviews.find(i => i.id === editingInterview.id);
    const fieldNames = { engineer_name: 'エンジニア名', company_name: '企業名', interview_date: '面談日', interview_time: '面談時刻', sales_person: '営業担当', status: 'ステータス', start_month: '開始月', response_deadline: '回答期限', notes: '備考' };
    const changes = Object.keys(fieldNames).filter(f => originalInterview[f] !== editingInterview[f]).map(f => ({ field: fieldNames[f], old: originalInterview[f] || '(なし)', new: editingInterview[f] || '(なし)' }));
    const updatedHistory = changes.length > 0 ? [...(originalInterview.history || []), { timestamp, user: editingInterview.sales_person || '不明', changes }] : (originalInterview.history || []);
    const payload = { ...editingInterview, history: updatedHistory };
    apiFetch(`/pp-interviews/${editingInterview.id}/`, { method: 'PUT', body: JSON.stringify(payload) })
      .then(res => res.json())
      .then(updated => {
        setInterviews(prev => prev.map(i => i.id === updated.id ? updated : i));
        setShowEditModal(false);
        setEditingInterview(null);
      });
  };

  // 編集モーダルを開く
  const openEditModal = (interview) => {
    setEditingInterview({ ...interview });
    setShowEditModal(true);
  };

  // 削除処理
  const handleDeleteInterview = (id) => {
    if (window.confirm('この面談情報を削除してもよろしいですか?')) {
      apiFetch(`/pp-interviews/${id}/`, { method: 'DELETE' })
        .then(() => setInterviews(prev => prev.filter(i => i.id !== id)));
    }
  };

  if (loading) {
    return <LoadingState />;
  }

  return (
    <div className="space-y-6 bg-prodia-background">
      {membersError && (
        <Alert variant="warning" title="営業メンバー情報を取得できませんでした">
          PP営業担当の一覧が表示できない状態です。SalesMember migration 適用後に再度お試しください。
        </Alert>
      )}
      <PageHeader
        title="PP営業進捗"
        description="カードをドラッグ&ドロップしてステータスを変更 · クリックで詳細表示"
        actions={
          <>
            <Button variant="secondary" onClick={() => { setArchiveStatus('成約'); setShowArchiveModal(true); }}>
              <i className="fas fa-history text-xs"></i>
              履歴
            </Button>
            <Button onClick={() => setShowNewInterviewModal(true)}>
              <i className="fas fa-plus text-xs"></i>
              新規面談追加
            </Button>
          </>
        }
        filters={
          <SalesProgressFiltersGrid>
            {/* 営業月（Kanban）/ 営業担当 — 実績・分析の selectedStatsMonth とは別管理 */}
            <SalesProgressFilterField icon="fa-calendar" label="営業月で絞り込み">
              <SalesMonthFilterSelect
                value={selectedSalesMonth}
                onChange={(e) => setSelectedSalesMonth(e.target.value)}
              />
            </SalesProgressFilterField>
            <SalesProgressFilterField icon="fa-user" label="営業担当で絞り込み">
              <PersonFilterSelect
                value={selectedSales}
                onChange={(e) => setSelectedSales(e.target.value)}
                options={salesPersonOptions}
                allLabel="すべての担当者"
              />
            </SalesProgressFilterField>
          </SalesProgressFiltersGrid>
        }
      />

      {/* 実績・分析 */}
      <SalesProgressStatsSection
        description="今月の面談・成約状況"
        statsMonth={selectedStatsMonth}
        onStatsMonthChange={(e) => setSelectedStatsMonth(e.target.value)}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard
            value={interviewCountForStats}
            label="総面談数"
            icon="fas fa-clipboard-list"
            accent="info"
          />
          <KpiCard
            value={inProgressCountForStats}
            label="進行中"
            icon="fas fa-clock"
            accent="warning"
          />
          <KpiCard
            value={engineersForStats}
            label="対象エンジニア"
            icon="fas fa-users"
            accent="primary"
          />
          <button
            type="button"
            onClick={() => {
              setArchiveStatus('成約');
              setShowArchiveModal(true);
            }}
            className="text-left w-full rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-prodia-primary/30"
          >
            <KpiCard
              value={wonCountForStats}
              label={selectedStatsMonth === 'all' ? '成約済み' : '成約数'}
              icon="fas fa-check-circle"
              accent="success"
            />
          </button>
        </div>
      </SalesProgressStatsSection>

      {/* カンバンボード */}
      <Card padding={false} className="overflow-hidden">
        <KanbanBoard scrollRef={scrollContainerRef}>
          {KANBAN_COLUMNS.map((column) => {
            const columnInterviews = getInterviewsByColumn(column.id);

            return (
              <KanbanColumn
                key={column.id}
                columnId={column.id}
                title={column.title}
                count={columnInterviews.length}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, column.id)}
              >
                {columnInterviews.map((interview) => (
                  <SalesKanbanCard
                    key={interview.id}
                    record={interview}
                    columnId={column.id}
                    primaryName={interview.engineer_name}
                    secondaryName={interview.company_name}
                    assignee={interview.sales_person}
                    isDragging={draggedItem?.id === interview.id}
                    formatStartMonth={formatStartMonth}
                    onDragStart={(e) => handleDragStart(e, interview)}
                    onDrag={handleDrag}
                    onDragEnd={handleDragEnd}
                    onOpenDetail={() => {
                      setSelectedInterview(interview);
                      setShowDetailModal(true);
                    }}
                    onOpenEdit={() => openEditModal(interview)}
                  />
                ))}
                {columnInterviews.length === 0 && <KanbanColumnEmpty />}
              </KanbanColumn>
            );
          })}
        </KanbanBoard>
      </Card>

      {/* 詳細モーダル */}
      {showDetailModal && selectedInterview && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowDetailModal(false)}>
          <div className="bg-prodia-surface rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-prodia-border" onClick={(e) => e.stopPropagation()}>
            <div className="border-b border-prodia-border p-6">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-xl font-semibold text-prodia-text-primary mb-1">{selectedInterview.engineer_name}</h3>
                  <p className="text-prodia-text-secondary">{selectedInterview.company_name}</p>
                </div>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="text-prodia-text-muted hover:bg-prodia-background rounded-lg p-2 transition-colors"
                >
                  <i className="fas fa-times text-xl"></i>
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                <div className="bg-prodia-background rounded-xl p-4 border border-prodia-border">
                  <p className="text-sm text-prodia-text-secondary mb-1">ステータス</p>
                  <Badge variant={badgeVariantForLabel(selectedInterview.status)} showIcon>
                    {selectedInterview.status}
                  </Badge>
                </div>
                <div className="bg-prodia-background rounded-xl p-4 border border-prodia-border">
                  <p className="text-sm text-prodia-text-secondary mb-1">営業担当</p>
                  <Badge variant="neutral">{selectedInterview.sales_person}</Badge>
                </div>
                <div className="bg-slate-50 rounded-xl p-4">
                  <p className="text-sm text-slate-600 mb-1">面談日</p>
                  <p className="font-bold text-slate-800">
                      {formatInterviewDate(selectedInterview.interview_date, selectedInterview.interview_time)}
                  </p>
                </div>
                <div className="bg-slate-50 rounded-xl p-4">
                  <p className="text-sm text-slate-600 mb-1">開始月</p>
                    <p className="font-bold text-slate-800">{formatStartMonth(selectedInterview.start_month)}</p>
                </div>
              </div>

              {selectedInterview.response_deadline && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <p className="text-sm text-amber-700 mb-1">回答期限</p>
                  <p className="font-bold text-amber-900">
                    {new Date(selectedInterview.response_deadline).toLocaleDateString('ja-JP')}
                  </p>
                </div>
              )}

              {selectedInterview.notes && (
                <div className="bg-prodia-background rounded-xl p-4 border border-prodia-border">
                  <p className="text-sm text-prodia-text-secondary mb-2">備考</p>
                  <p className="text-prodia-text-primary">{selectedInterview.notes}</p>
                </div>
              )}

              <div className="flex gap-2 pt-4">
                <Button 
                  className="flex-1"
                  onClick={() => {
                    setShowDetailModal(false);
                    openEditModal(selectedInterview);
                  }}
                >
                  <i className="fas fa-edit"></i>
                  編集
                </Button>
                <Button 
                  variant="danger"
                  onClick={() => {
                    setShowDetailModal(false);
                    handleDeleteInterview(selectedInterview.id);
                  }}
                >
                  <i className="fas fa-trash"></i>
                  削除
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 履歴モーダル */}
      {showArchiveModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowArchiveModal(false)}>
          <div className="bg-prodia-surface rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto border border-prodia-border" onClick={(e) => e.stopPropagation()}>
            <div className="border-b border-prodia-border p-6">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-xl font-semibold text-prodia-text-primary mb-1">{archiveStatus}履歴</h3>
                  <p className="text-sm text-prodia-text-secondary">画面上では消えた案件も、ここから履歴を確認できます。</p>
                </div>
                <button
                  onClick={() => setShowArchiveModal(false)}
                  className="text-prodia-text-muted hover:bg-prodia-background rounded-lg p-2 transition-colors"
                >
                  <i className="fas fa-times text-xl"></i>
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="flex gap-2 mb-4">
                {['成約', '見送り'].map((status) => (
                  <Button
                    key={status}
                    type="button"
                    size="sm"
                    variant={archiveStatus === status ? 'primary' : 'secondary'}
                    onClick={() => setArchiveStatus(status)}
                  >
                    {status}
                  </Button>
                ))}
              </div>

              <div className="space-y-3">
              {archiveInterviews.length > 0 ? archiveInterviews.map((interview) => (
                <button
                  key={interview.id}
                  type="button"
                  onClick={() => {
                    setShowArchiveModal(false);
                    setSelectedInterview(interview);
                    setShowDetailModal(true);
                  }}
                  className="w-full text-left bg-slate-50 hover:bg-slate-100 rounded-2xl p-4 border border-slate-200 transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-bold text-slate-800 truncate">{interview.engineer_name}</span>
                        <span className="text-xs text-slate-500">{interview.company_name}</span>
                      </div>
                      <div className="text-xs text-slate-500 space-y-1">
                        <p>面談日: {formatInterviewDate(interview.interview_date, interview.interview_time)}</p>
                        <p>開始月: {formatStartMonth(interview.start_month)}</p>
                      </div>
                    </div>
                    <div className="text-right text-xs text-prodia-text-muted flex-shrink-0">
                      <Badge variant={badgeVariantForLabel(archiveStatus)}>{archiveStatus}</Badge>
                      <p className="mt-1">{normalizeStatus(interview.status)}</p>
                    </div>
                  </div>
                </button>
              )) : (
                <div className="text-center py-12 text-slate-400">
                  <i className="fas fa-history text-4xl mb-3 opacity-20"></i>
                  <p className="text-sm">履歴はまだありません</p>
                </div>
              )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 新規面談追加モーダル */}
      {showNewInterviewModal && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={(e) => { if (e.target === e.currentTarget) { setShowNewInterviewModal(false); setNewInterviewErrors({}); setNewInterview({ engineer_name: '', company_name: '', interview_date: '', interview_time: '', sales_person: '', status: '', start_month: '', response_deadline: '', unit_price: '', notes: '' }); } }}
        >
          <div className="bg-prodia-surface rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-prodia-border">
            <div className="border-b border-prodia-border p-6">
              <h3 className="text-xl font-semibold text-prodia-text-primary flex items-center gap-3">
                <i className="fas fa-plus-circle text-prodia-primary"></i>
                新規面談追加
              </h3>
            </div>
            
            <div
              className="p-6 space-y-4"
              onFocusCapture={(e) => e.target.scrollIntoView({ behavior: 'smooth', block: 'nearest' })}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">エンジニア名 <span className="text-red-500">*</span></label>
                  <EngineerNameAutocomplete
                    value={newInterview.engineer_name}
                    onChange={(name) => { setNewInterview({ ...newInterview, engineer_name: name }); if (newInterviewErrors.engineer_name) setNewInterviewErrors(prev => ({ ...prev, engineer_name: '' })); }}
                    engineerNames={engineerNames}
                    className={`w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-prodia-primary/30 focus:border-prodia-primary ${
                      newInterviewErrors.engineer_name ? 'border-red-400 bg-red-50' : 'border-slate-300'
                    }`}
                    placeholder="例: 浜田太郎"
                  />
                  {newInterviewErrors.engineer_name && (
                    <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                      <i className="fas fa-exclamation-circle"></i>
                      {newInterviewErrors.engineer_name}
                    </p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">企業名 <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={newInterview.company_name}
                    onChange={(e) => { setNewInterview({ ...newInterview, company_name: e.target.value }); if (newInterviewErrors.company_name) setNewInterviewErrors(prev => ({ ...prev, company_name: '' })); }}
                    className={`w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-prodia-primary/30 focus:border-prodia-primary ${
                      newInterviewErrors.company_name ? 'border-red-400 bg-red-50' : 'border-slate-300'
                    }`}
                    placeholder="例: 株式会社メイテツコム"
                  />
                  {newInterviewErrors.company_name && (
                    <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                      <i className="fas fa-exclamation-circle"></i>
                      {newInterviewErrors.company_name}
                    </p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">面談日</label>
                  <SmartDateInput
                    name="interview_date"
                    value={newInterview.interview_date}
                    onChange={(e) => setNewInterview({ ...newInterview, interview_date: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-prodia-primary/30 focus:border-prodia-primary"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">面談時間</label>
                  <SmartTimeInput
                    name="interview_time"
                    value={newInterview.interview_time}
                    onChange={(e) => setNewInterview({ ...newInterview, interview_time: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-prodia-primary/30 focus:border-prodia-primary"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">営業担当 <span className="text-red-500">*</span></label>
                  <select
                    value={newInterview.sales_person}
                    onChange={(e) => { setNewInterview({ ...newInterview, sales_person: e.target.value }); if (newInterviewErrors.sales_person) setNewInterviewErrors(prev => ({ ...prev, sales_person: '' })); }}
                    className={`w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-prodia-primary/30 focus:border-prodia-primary ${
                      newInterviewErrors.sales_person ? 'border-red-400 bg-red-50' : 'border-slate-300'
                    }`}
                  >
                    <option value="">選択してください</option>
                    {salesPersonOptions.map((name) => (
                      <option key={name} value={name}>{name}</option>
                    ))}
                  </select>
                  {newInterviewErrors.sales_person && (
                    <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                      <i className="fas fa-exclamation-circle"></i>
                      {newInterviewErrors.sales_person}
                    </p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">ステータス <span className="text-red-500">*</span></label>
                  <select
                    value={newInterview.status}
                    onChange={(e) => { setNewInterview({ ...newInterview, status: e.target.value }); if (newInterviewErrors.status) setNewInterviewErrors(prev => ({ ...prev, status: '' })); }}
                    className={`w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-prodia-primary/30 focus:border-prodia-primary ${
                      newInterviewErrors.status ? 'border-red-400 bg-red-50' : 'border-slate-300'
                    }`}
                  >
                    <option value="">選択してください</option>
                    <option value="日程調整中">日程調整中</option>
                    <option value="日程調整中（1/2）">日程調整中（1/2）</option>
                    <option value="日程調整中（2/2）">日程調整中（2/2）</option>
                    <option value="面談予定">面談予定</option>
                    <option value="面談予定（1/2）">面談予定（1/2）</option>
                    <option value="面談予定（2/2）">面談予定（2/2）</option>
                    <option value="面談済み／回答待ち">面談済み／回答待ち</option>
                    <option value="面談済み／回答待ち（1/2）">面談済み／回答待ち（1/2）</option>
                    <option value="面談済み／回答待ち（2/2）">面談済み／回答待ち（2/2）</option>
                    <option value="成約">成約</option>
                    <option value="見送り">見送り</option>
                  </select>
                  {newInterviewErrors.status && (
                    <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                      <i className="fas fa-exclamation-circle"></i>
                      {newInterviewErrors.status}
                    </p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">開始月 <span className="text-red-500">*</span></label>
                  <SmartMonthInput
                    name="start_month"
                    value={newInterview.start_month}
                    onChange={(e) => { setNewInterview({ ...newInterview, start_month: e.target.value }); if (newInterviewErrors.start_month) setNewInterviewErrors(prev => ({ ...prev, start_month: '' })); }}
                    className={`w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-prodia-primary/30 focus:border-prodia-primary ${
                      newInterviewErrors.start_month ? 'border-red-400 bg-red-50' : 'border-slate-300'
                    }`}
                  />
                  {newInterviewErrors.start_month && (
                    <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                      <i className="fas fa-exclamation-circle"></i>
                      {newInterviewErrors.start_month}
                    </p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">回答期限</label>
                  <SmartDateInput
                    name="response_deadline"
                    value={newInterview.response_deadline}
                    onChange={(e) => setNewInterview({ ...newInterview, response_deadline: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-prodia-primary/30 focus:border-prodia-primary"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">単価</label>
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 font-medium">¥</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={formatUnitPrice(newInterview.unit_price)}
                      onChange={(e) => {
                        const normalized = e.target.value.replace(/[０-９]/g, c => String.fromCharCode(c.charCodeAt(0) - 0xFEE0));
                        const raw = normalized.replace(/,/g, '');
                        if (raw === '' || /^\d*\.?\d*$/.test(raw)) {
                          setNewInterview({ ...newInterview, unit_price: raw });
                        }
                      }}
                      className="w-full pl-8 pr-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-prodia-primary/30 focus:border-prodia-primary"
                      placeholder="例: 80"
                    />
                  </div>
                  <span className="text-sm font-medium text-slate-600 whitespace-nowrap">万円</span>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">備考</label>
                <textarea
                  value={newInterview.notes}
                  onChange={(e) => setNewInterview({ ...newInterview, notes: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-prodia-primary/30 focus:border-prodia-primary"
                  rows="3"
                  placeholder="メモや特記事項を入力"
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button onClick={handleAddInterview} className="flex-1">
                  <i className="fas fa-check"></i>
                  追加
                </Button>
                <Button
                  variant="secondary"
                  className="flex-1"
                  onClick={() => {
                    setShowNewInterviewModal(false);
                    setNewInterviewErrors({});
                    setNewInterview({ engineer_name: '', company_name: '', interview_date: '', interview_time: '', sales_person: '', status: '', start_month: '', response_deadline: '', unit_price: '', notes: '' });
                  }}
                >
                  <i className="fas fa-times"></i>
                  キャンセル
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 編集モーダル */}
      {showEditModal && editingInterview && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-prodia-surface rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto border border-prodia-border">
            <div className="border-b border-prodia-border p-6">
              <h3 className="text-xl font-semibold text-prodia-text-primary flex items-center gap-3">
                <i className="fas fa-edit text-prodia-primary"></i>
                面談情報編集
              </h3>
            </div>
            
            <div className="p-6 space-y-6">
              {/* 編集フォーム */}
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">エンジニア名 *</label>
                    <EngineerNameAutocomplete
                      value={editingInterview.engineer_name}
                      onChange={(name) => setEditingInterview({ ...editingInterview, engineer_name: name })}
                      engineerNames={engineerNames}
                      className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-prodia-primary/30 focus:border-prodia-primary"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">企業名 *</label>
                    <input
                      type="text"
                      value={editingInterview.company_name}
                      onChange={(e) => setEditingInterview({ ...editingInterview, company_name: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-prodia-primary/30 focus:border-prodia-primary"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">面談日</label>
                    <input
                      type="date"
                      value={editingInterview.interview_date}
                      onChange={(e) => setEditingInterview({ ...editingInterview, interview_date: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-prodia-primary/30 focus:border-prodia-primary"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">面談時間</label>
                    <input
                      type="time"
                      value={editingInterview.interview_time}
                      onChange={(e) => setEditingInterview({ ...editingInterview, interview_time: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-prodia-primary/30 focus:border-prodia-primary"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">営業担当 *</label>
                    <select
                      value={editingInterview.sales_person}
                      onChange={(e) => setEditingInterview({ ...editingInterview, sales_person: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-prodia-primary/30 focus:border-prodia-primary"
                    >
                      {salesPersonOptions.map((name) => (
                        <option key={name} value={name}>{name}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">ステータス *</label>
                    <select
                      value={editingInterview.status}
                      onChange={(e) => setEditingInterview({ ...editingInterview, status: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-prodia-primary/30 focus:border-prodia-primary"
                    >
                      <option value="日程調整中">日程調整中</option>
                      <option value="日程調整中（1/2）">日程調整中（1/2）</option>
                      <option value="日程調整中（2/2）">日程調整中（2/2）</option>
                      <option value="面談予定">面談予定</option>
                      <option value="面談予定（1/2）">面談予定（1/2）</option>
                      <option value="面談予定（2/2）">面談予定（2/2）</option>
                      <option value="面談済み／回答待ち">面談済み／回答待ち</option>
                      <option value="面談済み／回答待ち（1/2）">面談済み／回答待ち（1/2）</option>
                      <option value="面談済み／回答待ち（2/2）">面談済み／回答待ち（2/2）</option>
                      <option value="成約">成約</option>
                      <option value="見送り">見送り</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">開始月 *</label>
                    <input
                      type="month"
                      value={editingInterview.start_month}
                      onChange={(e) => setEditingInterview({ ...editingInterview, start_month: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-prodia-primary/30 focus:border-prodia-primary"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">回答期限</label>
                    <input
                      type="date"
                      value={editingInterview.response_deadline}
                      onChange={(e) => setEditingInterview({ ...editingInterview, response_deadline: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-prodia-primary/30 focus:border-prodia-primary"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">単価（万円）</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 font-medium">¥</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={formatUnitPrice(editingInterview.unit_price || '')}
                      onChange={(e) => {
                        const normalized = e.target.value.replace(/[０-９]/g, c => String.fromCharCode(c.charCodeAt(0) - 0xFEE0));
                        const raw = normalized.replace(/,/g, '');
                        if (raw === '' || /^\d*\.?\d*$/.test(raw)) {
                          setEditingInterview({ ...editingInterview, unit_price: raw });
                        }
                      }}
                      className="w-full pl-8 pr-12 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-prodia-primary/30 focus:border-prodia-primary"
                      placeholder="例: 80"
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 text-sm">万円</span>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">備考</label>
                  <textarea
                    value={editingInterview.notes}
                    onChange={(e) => setEditingInterview({ ...editingInterview, notes: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-prodia-primary/30 focus:border-prodia-primary"
                    rows="3"
                  />
                </div>
              </div>

              {/* 編集履歴 */}
              {editingInterview.history && editingInterview.history.length > 0 && (
                <div className="border-t pt-6">
                  <h4 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
                    <i className="fas fa-history text-blue-500"></i>
                    編集履歴
                  </h4>
                  <div className="space-y-3 max-h-60 overflow-y-auto">
                    {editingInterview.history.slice().reverse().map((entry, index) => (
                      <div key={index} className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <i className="fas fa-user-circle text-blue-500"></i>
                            <span className="font-medium text-slate-700">{entry.user}</span>
                          </div>
                          <span className="text-sm text-slate-500">{entry.timestamp}</span>
                        </div>
                        <div className="space-y-1">
                          {entry.changes.map((change, changeIndex) => (
                            <div key={changeIndex} className="text-sm">
                              <span className="font-medium text-slate-700">{change.field}:</span>
                              {change.old && change.old !== '(なし)' && (
                                <span className="text-red-600 line-through ml-2">{change.old}</span>
                              )}
                              {change.new && change.new !== '(なし)' && (
                                <span className="text-green-600 font-medium ml-2">→ {change.new}</span>
                              )}
                              {change.new === '新規登録' && (
                                <span className="text-blue-600 font-medium ml-2">{change.new}</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="flex gap-3 pt-4 border-t border-prodia-border">
                <Button onClick={handleEditInterview} className="flex-1">
                  <i className="fas fa-save"></i>
                  保存
                </Button>
                <Button
                  variant="secondary"
                  className="flex-1"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingInterview(null);
                  }}
                >
                  <i className="fas fa-times"></i>
                  キャンセル
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
